import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return {
        el: to.hash,
        top: 82,
        behavior: 'smooth'
      }
    }
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/home/HomePage.vue'),
      meta: { requiresAuth: false, showBottomNav: true }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutPage.vue'),
      meta: {
        requiresAuth: false,
        showBottomNav: false
      }
    },
    {
      path: '/notice',
      name: 'notice',
      component: () => import('../views/notice/NoticePage.vue'),
      meta: {
        requiresAuth: false,
        showBottomNav: false,
        mobileTitle: '更新公告'
      }
    },
    {
      path: '/market',
      name: 'account-market',
      redirect: to => ({
        path: '/',
        hash: '#home-market-section',
        query: to.query
      }),
      meta: {
        requiresAuth: false,
        showBottomNav: true,
        mobileTitle: '市场',
        keepAlive: true  // 启用 keep-alive 缓存
      }
    },
    {
      path: '/deltaforce-auth-bridge',
      name: 'deltaforce-auth-bridge',
      component: () => import('../views/accounts/DeltaForceAuthBridgePage.vue'),
      meta: {
        requiresAuth: true,
        showBottomNav: false,
        showNavBar: false,
        mobileTitle: '三角洲授权'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/login/LoginPage.vue'),
      meta: {
        requiresAuth: false,
        guestOnly: true,
        showBottomNav: false,
        mobileTitle: '登录'
      }
    },
    {
      path: '/account',
      children: [
        {
          path: 'detail/:id',
          name: 'account-detail',
          component: () => import('../views/accounts/AccountDetailPage.vue'),
          meta: {
            requiresAuth: false,
            showBottomNav: false,
            mobileTitle: '账号详情',
            pageHeader: {
              title: '账号详情',
              icon: 'FileTextOutlined',
              description: '查看账号详细信息和租赁条件'
            }
          }
        },
        {
          path: 'my',
          name: 'my-accounts',
          component: () => import('../views/accounts/MyAccountsPage.vue'),
          meta: {
            requiresAuth: true,
            showBottomNav: false,
            mobileTitle: '我的账号'
          }
        },
        {
          path: 'publish',
          name: 'publish-account',
          component: () => import('../views/accounts/PublishAccountPage.vue'),
          meta: {
            requiresAuth: false,
            title: '发布账号',
            showBottomNav: false,
            showNavBar: false,
            mobileTitle: '发布'
          }
        },
        {
          path: 'edit/:id',
          name: 'edit-account',
          component: () => import('../views/accounts/PublishAccountPage.vue'),
          meta: {
            requiresAuth: true,
            title: '编辑账号',
            showBottomNav: false,
            showNavBar: false,
            mobileTitle: '编辑账号'
          }
        }
      ]
    },
    {
      path: '/favorites',
      name: 'favorites',
      component: () => import('../views/favorites/FavoritesPage.vue'),
      meta: {
        requiresAuth: true,
        showBottomNav: true,
        mobileTitle: '收藏',
        keepAlive: true
      }
    },
    {
      path: '/notifications',
      name: 'user-notifications-list',
      component: () => import('../views/notifications/UserNotificationsPage.vue'),
      meta: {
        requiresAuth: true,
        showBottomNav: false,
        mobileTitle: '通知'
      }
    },
    {
      path: '/notifications/detail/:id',
      name: 'user-notification-detail',
      component: () => import('../views/notifications/UserNotificationDetailPage.vue'),
      meta: {
        requiresAuth: true,
        showBottomNav: false,
        mobileTitle: '通知详情'
      }
    },
    {
      path: '/support',
      name: 'support-chat',
      component: () => import('../views/support/SupportChatPage.vue'),
      meta: {
        requiresAuth: true,
        showBottomNav: false,
        mobileTitle: '在线客服'
      }
    },
    {
      path: '/support/account-link/:accountId',
      name: 'support-account-link',
      component: () => import('../views/support/SupportAccountLinkPage.vue'),
      meta: {
        requiresAuth: true,
        showBottomNav: false,
        mobileTitle: '账号链接'
      }
    },
    {
      path: '/order',
      children: [
        {
          path: 'list',
          name: 'order-list',
          component: () => import('../views/orders/OrderListPage.vue'),
          meta: {
            requiresAuth: true,
            showBottomNav: true,
            mobileTitle: '订单'
          }
        },
        {
          path: 'detail/:id',
          name: 'order-detail',
          component: () => import('../views/orders-detail/OrderDetailPage.vue'),
          meta: {
            requiresAuth: true,
            showBottomNav: false,
            mobileTitle: '订单详情',
            pageHeader: {
              title: '订单详情',
              icon: 'FileTextOutlined',
              description: '查看订单详细信息和状态'
            }
          }
        }
      ]
    },
    {
      path: '/payment',
      children: [
        {
          path: 'confirm/:orderId',
          name: 'payment-confirm',
          component: () => import('../views/payment/PaymentConfirmPage.vue'),
          meta: {
            requiresAuth: true,
            title: '确认支付',
            showBottomNav: false,
            mobileTitle: '确认支付',
            pageHeader: {
              title: '确认支付',
              icon: 'CreditCardOutlined',
              description: '确认订单信息并完成支付'
            }
          }
        }
      ]
    },
    {
      path: '/profile',
      children: [
        {
          path: 'center',
          name: 'profile-center',
          component: () => import('../views/profile/ProfileCenterPage-Mobile.vue'),
          meta: {
            requiresAuth: false,
            showBottomNav: true,
            mobileTitle: '我的'
          }
        },
        {
          path: 'info',
          name: 'profile-info',
          component: () => import('../views/profile/ProfileInfoPage.vue'),
          meta: {
            requiresAuth: true,
            showBottomNav: false,
            mobileTitle: '个人设置',
            pageHeader: {
              title: '个人设置',
              icon: 'SettingOutlined',
              description: '管理您的个人信息和安全设置'
            }
          }
        },
        {
          path: 'invitations',
          name: 'profile-invitations',
          component: () => import('../views/user/InvitationCenterPage.vue'),
          meta: {
            requiresAuth: true,
            showBottomNav: false,
            mobileTitle: '邀请拉新'
          }
        },
        {
          path: 'wallet',
          name: 'wallet',
          component: () => import('../views/wallet/WalletPage.vue'),
          meta: {
            requiresAuth: true,
            showBottomNav: false,
            mobileTitle: '我的钱包'
          }
        }
      ]
    },
    {
      path: '/agent',
      children: [
        {
          path: '',
          name: 'agent-dashboard',
          component: () => import('../views/agent/AgentDashboardPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAgent: true,
            title: '商户中心',
            showBottomNav: false,
            mobileTitle: '商户中心',
            pageHeader: {
              title: '商户中心',
              icon: 'ShareAltOutlined',
              description: '商户数据统计和管理'
            }
          }
        }
      ]
    },
    {
      path: '/admin',
      children: [
        {
          path: '',
          name: 'admin-dashboard',
          component: () => import('../view-admin/dashboard/DashboardPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '管理后台',
            showBottomNav: false,
            mobileTitle: '管理后台',
            pageHeader: {
              title: '管理后台',
              icon: 'CrownOutlined',
              description: '系统管理和数据统计'
            }
          }
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('../view-admin/users/UserManagementPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '用户管理',
            showBottomNav: false,
            pageHeader: {
              title: '用户管理',
              icon: 'UserOutlined',
              description: '管理平台用户和权限'
            }
          }
        },
        {
          path: 'accounts',
          children: [
            {
              path: 'audit',
              name: 'admin-accounts-audit',
              component: () => import('../view-admin/accounts/AccountAuditPage.vue'),
              meta: {
                requiresAuth: true,
                requiresAdmin: true,
                title: '账号审核',
                showBottomNav: false,
                pageHeader: {
                  title: '账号审核',
                  icon: 'SecurityScanOutlined',
                  description: '审核用户发布的游戏账号'
                }
              }
            }
            ,
            {
              path: 'manage',
              name: 'admin-accounts-manage',
              component: () => import('../view-admin/accounts/AccountManagementPage.vue'),
              meta: {
                requiresAuth: true,
                requiresAdmin: true,
                title: '账号管理',
                showBottomNav: false,
                pageHeader: {
                  title: '账号管理',
                  icon: 'SettingOutlined',
                  description: '管理平台所有游戏账号'
                }
              }
            }
          ]
        },
        {
          path: 'assets/auto-fetch',
          name: 'admin-assets-auto-fetch',
          component: () => import('../view-admin/assets/DeltaForceAutoFetchLabPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '三角洲数据拉取',
            showBottomNav: false,
            pageHeader: {
              title: '三角洲数据拉取',
              icon: 'AppstoreOutlined',
              description: '后台过渡验证资产识别和扫码拉取结果'
            }
          }
        },
        {
          path: 'refunds',
          name: 'admin-refunds',
          component: () => import('../view-admin/refunds/RefundManagementPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '退款管理',
            showBottomNav: false,
            pageHeader: {
              title: '退款管理',
              icon: 'UndoOutlined',
              description: '处理用户退款申请和审核'
            }
          }
        },
        {
          path: 'orders',
          name: 'admin-orders',
          component: () => import('../view-admin/orders/OrderManagementPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '订单管理',
            showBottomNav: false,
            pageHeader: {
              title: '订单管理',
              icon: 'FileTextOutlined',
              description: '管理平台所有订单和支付状态'
            }
          }
        },
        {
          path: 'invitations',
          name: 'admin-invitations',
          component: () => import('../view-admin/invitations/InvitationCenterAdminPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '邀请中心',
            showBottomNav: false,
            pageHeader: {
              title: '邀请中心',
              icon: 'ShareAltOutlined',
              description: '查看拉新与邀请明细数据'
            }
          }
        },
        {
          path: 'wallet',
          name: 'admin-wallet',
          component: () => import('../view-admin/wallet/WalletManagementPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '钱包管理',
            showBottomNav: false,
            pageHeader: {
              title: '钱包管理',
              icon: 'WalletOutlined',
              description: '管理用户钱包和财务记录'
            }
          }
        },
        {
          path: 'finance',
          name: 'admin-finance-report',
          component: () => import('../view-admin/finance/FinanceReportPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '财务报表',
            showBottomNav: false,
            pageHeader: {
              title: '财务报表',
              icon: 'BarChartOutlined',
              description: '查看平台收支情况和财务数据'
            }
          }
        },
        {
          path: 'withdrawal-requests',
          name: 'admin-withdrawal-requests',
          component: () => import('../view-admin/withdrawal/WithdrawalRequestManagementPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '提现申请管理',
            showBottomNav: false,
            pageHeader: {
              title: '提现申请管理',
              icon: 'BankOutlined',
              description: '审批和处理用户提现申请'
            }
          }
        },
        {
          path: 'support/chat',
          name: 'admin-support-chat',
          component: () => import('../view-admin/support/AdminSupportChatPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '在线客服',
            showBottomNav: false,
            pageHeader: {
              title: '在线客服',
              icon: 'CustomerServiceOutlined',
              description: '处理用户站内咨询和租赁问题'
            }
          }
        },
        {
          path: 'support/auto-replies',
          name: 'admin-support-auto-replies',
          component: () => import('../view-admin/support/AdminSupportAutoReplyPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '客服回复库',
            showBottomNav: false,
            pageHeader: {
              title: '客服回复库',
              icon: 'MessageOutlined',
              description: '管理关键词回复和智能回复内容'
            }
          }
        },
        {
          path: 'home-banners',
          name: 'admin-home-banners',
          component: () => import('../view-admin/settings/HomeBannerManagementPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '首页轮播图',
            showBottomNav: false,
            pageHeader: {
              title: '首页轮播图',
              icon: 'PictureOutlined',
              description: '管理首页主轮播图、排序和展示端'
            }
          }
        },
        {
          path: 'settings',
          name: 'admin-settings',
          component: () => import('../view-admin/settings/SystemSettingsPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '系统设置',
            showBottomNav: false,
            pageHeader: {
              title: '系统设置',
              icon: 'SettingOutlined',
              description: '配置系统参数和平台设置'
            }
          }
        },
        {
          path: 'consumption-plans',
          name: 'admin-consumption-plans',
          component: () => import('../view-admin/settings/ConsumptionPlanSettingsPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '消耗速度配置',
            showBottomNav: false,
            pageHeader: {
              title: '消耗速度配置',
              icon: 'CalculatorOutlined',
              description: '单独配置10M/20M/30M每日消耗、价格比例和特惠区规则'
            }
          }
        },
        {
          path: 'publish-prompts',
          name: 'admin-publish-prompts',
          component: () => import('../view-admin/settings/PublishPromptSettingsPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '发布文案',
            showBottomNav: false,
            pageHeader: {
              title: '发布文案',
              icon: 'FileTextOutlined',
              description: '单独配置发布页各字段下方提示文案'
            }
          }
        },
        {
          path: 'agents',
          name: 'admin-agents',
          component: () => import('../view-admin/agents/AgentManagementPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '代理商管理',
            showBottomNav: false,
            pageHeader: {
              title: '代理商管理',
              icon: 'ShareAltOutlined',
              description: '管理代理商和分成结算'
            }
          }
        },
        {
          path: 'permissions',
          name: 'admin-permissions',
          component: () => import('../view-admin/permissions/PermissionCenterPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '权限管理中心',
            showBottomNav: false,
            pageHeader: {
              title: '权限管理中心',
              icon: 'SafetyOutlined',
              description: '统一管理系统权限、角色和用户授权'
            }
          }
        },
        {
          path: 'notifications/send',
          name: 'admin-notifications-send',
          component: () => import('../view-admin/notifications/AdminNotificationSendPage.vue'),
          meta: {
            requiresAuth: true,
            requiresAdmin: true,
            title: '发送通知',
            showBottomNav: false,
            pageHeader: {
              title: '发送通知',
              icon: 'BellOutlined',
              description: '向指定用户批量发送站内通知'
            }
          }
        }
      ]
    }
  ]
})

// 预加载关键路由
const preloadRoutes = [
  () => import('../views/market/AccountMarketPage.vue'),
  () => import('../views/login/LoginPage.vue')
]

// 在空闲时预加载
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    preloadRoutes.forEach(route => {
      route().catch(() => {
        // 预加载失败不影响正常使用
      })
    })
  })
}

// 全局前置守卫 - 适配JWT认证
router.beforeEach(async (to, from, next) => {
  try {
    // 获取认证store
    const authStore = useAuthStore()

    // 确保认证状态已初始化
    let retryCount = 0
    while (retryCount < 3) {
      try {
        // 尝试访问authStore的属性来确认其已初始化
        void authStore.isAuthenticated
        break
      } catch {
        await new Promise(resolve => setTimeout(resolve, 50))
        retryCount++
      }
    }

    // 检查JWT令牌状态
    let isLoggedIn = authStore.isAuthenticated

    // 如果有JWT令牌但认证状态为false，尝试验证令牌有效性
    if (!isLoggedIn && authStore.accessToken) {
      try {
        // 尝试验证JWT令牌有效性（通过检查认证状态）
        isLoggedIn = await authStore.checkAuthStatus()
        if (!isLoggedIn) {
          authStore.clearUserAuth()
        } else {
        }
      } catch (error) {
        console.error('❌ JWT令牌验证失败:', error)
        authStore.clearUserAuth()
        isLoggedIn = false
      }
    }

    // 如果是需要管理员权限的页面
    if (to.meta.requiresAdmin) {
      if (!isLoggedIn) {
        next({
          path: '/login',
          query: { redirect: to.fullPath }
        })
        return
      }

      if (!authStore.canAccessAdmin) {
        next('/')
        return
      }
    }

    // 如果是需要代理商权限的页面
    if (to.meta.requiresAgent) {
      if (!isLoggedIn) {
        next({
          path: '/login',
          query: { redirect: to.fullPath }
        })
        return
      }

      if (!authStore.canAccessAgent) {
        next('/')
        return
      }
    }

    // 如果是需要认证的页面
    if (to.meta.requiresAuth) {
      if (!isLoggedIn) {
        next({
          path: '/login',
          query: { redirect: to.fullPath }
        })
        return
      }
    }

    // 如果是仅访客页面（如登录页面）
    if (to.meta.guestOnly && isLoggedIn) {
      const requestedRedirect = (to.query.redirect as string) || ''
      if (requestedRedirect.startsWith('/admin') && !authStore.canAccessAdmin) {
        next('/#home-market-section')
        return
      }
      const redirectPath = requestedRedirect || (authStore.canAccessAdmin ? '/admin' : '/#home-market-section')
      next(redirectPath)
      return
    }

    next()

  } catch (error) {
    console.error('❌ 路由守卫检查出错:', error)
    // 路由检查本身的异常不能证明令牌失效，保留当前登录状态。
    next()
  }
})

// 全局后置钩子
router.afterEach((to) => {
  // 更新页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - 龙珠电竞`
  } else {
    document.title = '龙珠电竞'
  }
})

// JWT认证过期处理 - 监听全局认证过期事件
if (typeof window !== 'undefined') {
  window.addEventListener('auth-expired', (event: Event) => {
    const customEvent = event as CustomEvent
    console.log('🔐 收到认证过期事件:', customEvent.detail)

    // 仅当当前路由需要登录时才跳转到登录页，避免公共页面被误伤
    const current = router.currentRoute.value
    const requiresAuth = !!current.meta?.requiresAuth || !!current.meta?.requiresAdmin || !!current.meta?.requiresAgent
    if (current.name !== 'login' && requiresAuth) {
      const currentPath = current.fullPath
      router.push({
        path: '/login',
        query: { redirect: currentPath }
      }).catch(err => {
        console.error('❌ 跳转登录页失败:', err)
      })
    }

  })
}

export default router
